import { BACKEND_API_URL } from "@/shared/config/app";
import { siteSessionToken } from "@/shared/api/adminHeaders";
import { achLang } from "../lib/lang";
import { ACH_SPHERES } from "../model/types";
import type {
    AchCrestHint,
    AchGoal,
    AchGoalStep,
    AchItem,
    AchLadderState,
    AchLevel,
    AchSphere,
    AchievementsView,
} from "../model/types";
import {
    achKey,
    compact,
    imgId,
    int,
    isObj,
    list,
    normForm,
    normLadders,
    normRow,
    normTop,
    num,
    pct,
    str,
    variantKey,
} from "./normView";

/**
 * Свои достижения: GET /api/achievements/app/me?lang=… (site_server) — тот же свой
 * вид, что /api/achievements/me (PLAN §3.6), и сверх него готовые строки «что
 * сделать → что получите» на языке страницы: nearest[] (начатое) и goals[]
 * (начатое и ещё не начатое — «ступень 1»; сервер присылает их не сразу —
 * пока их нет, сайт считает «Ближайшие» сам). Строки — те же, что пишет сайт
 * (services_fastapi/achievements_nearest — порт lib/viewModel.ts).
 *
 * Вход — подписанный site_session_token в Authorization: Bearer, как у
 * остальных запросов к site_server. Пока достижения видят только
 * администраторы, всем остальным сервер отвечает 404 — это не поломка, а
 * «достижений для вас нет»: null. Нет токена — тоже null, без запроса;
 * «войти как» и токен одной страницы сервер не пускает (401 / 403) — тоже null.
 *
 * Ответ разбирается строго: чего нет в договоре — отбрасывается; ключи,
 * которых нет в каталоге, отсеивает уже модель страницы (lib/viewModel.ts).
 * value / next / points есть только здесь, в своём виде (R2, R3).
 */

export class AchievementsApiError extends Error {
    status: number | null;
    constructor(status: number | null, message?: string) {
        super(message || `achievements: ${status ?? "network"}`);
        this.status = status;
    }
}

/**
 * Сколько ждать ответа. Было 15 с — а на тестовом стенде .life у человека с 60 друзьями сервер
 * считал социальную сферу ≈15,2 с, и страница писала «Не загрузилось», хотя ответ был верный
 * (проверка на 200 людях 23.10, находка 5; у .com сайт ждал те же 15 с). Полминуты — с запасом
 * над медленным ответом, но не бесконечно: висящее соединение всё же обрывается, и человек
 * видит «Повторить».
 */
export const ACH_TIMEOUT_MS = 30_000;
/**
 * «Повторить» ждёт дольше: человек сам попросил ещё раз, значит готов подождать, а сервер,
 * не уложившийся в полминуты, тем же сроком снова не уложился бы — и кнопка не помогала бы.
 */
export const ACH_RETRY_TIMEOUT_MS = 60_000;

const normItem = (v: unknown): AchItem | null => {
    if (!isObj(v)) return null;
    const k = achKey(v.key);
    if (!k) return null;
    return compact({
        key: k,
        tier: int(v.tier, 0, 99),
        at: str(v.at, 40),
        value: num(v.value),
        next: num(v.next),
        share: pct(v.share),
    });
};

const normLevel = (v: unknown): AchLevel | null => {
    if (!isObj(v)) return null;
    const level = int(v.level, 0, 99);
    if (level === undefined) return null;
    // Договор §3.6 зовёт порог next; ADDENDUM §3 — next_points. Принимаем оба.
    return compact({ level, at: str(v.at, 40), points: num(v.points), next: num(v.next) ?? num(v.next_points) });
};

/**
 * Лестница тренировок у себя (ADDENDUM §6–§7): {level 0…6, at?, value?, next?, variant?, img}:
 * value / next — практики по плану (у общего уровня — недели по плану), только здесь.
 */
const normLadder = (v: unknown): AchLadderState | null => {
    if (!isObj(v)) return null;
    const level = int(v.level, 0, 99);
    if (level === undefined) return null;
    return compact({
        level,
        at: str(v.at, 40),
        value: num(v.value),
        next: num(v.next),
        variant: variantKey(v.variant),
        img: imgId(v.img),
    });
};

const GOAL_ID_RE = /^(lvl:)?[a-z0-9_]{1,64}$/;
const GOAL_KINDS = new Set(["tier", "pace", "level", "ladder"]);
const LINK_KEYS = new Set(["workouts", "courses", "meetings", "invite", "search", "lectures", "reviews"]);
const ANCHORS = new Set(["workouts", "social", "referrals", "learning", "contrib", "all"]);
/** Ступеней у достижения 4, уровней 6 — с запасом, но не бесконечно. */
const MAX_STEPS = 12;

/** Ступень строки цели: {n, label, img}. Без номера или подписи — нет ступени; чужая картинка — без неё. */
const normStep = (v: unknown): AchGoalStep | null => {
    if (!isObj(v)) return null;
    const n = int(v.n, 1, 99);
    const label = str(v.label, 120);
    if (n === undefined || !label) return null;
    return compact({ n, label, img: imgId(v.img) });
};

/**
 * Строка nearest[] / goals[]: {id, kind, key, sphere, name, n, of, value, next, remaining,
 * ratio, line, img, link, anchor, started, what, current, steps, share_next}. Без id, вида или
 * строки — нет строки; started нет — started (у nearest его нет: там всё начатое); what /
 * current / steps — пояснение и ступени для окна, share_next — доля участников со ступенью n
 * (профиль v2 §2.2; сервер присылает их не сразу — нет, так нет).
 */
export const normGoal = (v: unknown): AchGoal | null => {
    if (!isObj(v)) return null;
    const id = typeof v.id === "string" && GOAL_ID_RE.test(v.id) ? v.id : undefined;
    const kind = typeof v.kind === "string" && GOAL_KINDS.has(v.kind) ? v.kind : undefined;
    const key = achKey(v.key);
    const line = str(v.line, 400);
    if (!id || !kind || !key || !line) return null;
    const ratio = num(v.ratio);
    return compact({
        id,
        kind,
        key,
        sphere: achKey(v.sphere) || "",
        name: str(v.name, 160) || "",
        n: int(v.n, 0, 99) ?? 0,
        of: int(v.of, 0, 99) ?? 0,
        value: num(v.value),
        next: num(v.next),
        remaining: num(v.remaining) ?? 0,
        ratio: ratio === undefined ? 0 : Math.min(1, Math.max(0, ratio)),
        line,
        img: imgId(v.img),
        link: typeof v.link === "string" && LINK_KEYS.has(v.link) ? v.link : undefined,
        anchor: typeof v.anchor === "string" && ANCHORS.has(v.anchor) ? v.anchor : undefined,
        started: v.started !== false,
        what: str(v.what, 400),
        current: int(v.current, 0, 99),
        steps: Array.isArray(v.steps) ? list(v.steps, normStep).slice(0, MAX_STEPS) : undefined,
        // null — ступени ещё ни у кого (сводка есть, держателей нет); нет поля — доля не известна.
        shareNext: v.share_next === null ? null : pct(v.share_next),
    });
};

/** Ключ «Своего герба» (профиль v2 §3.7): его строка в goals[] — вид "once", своя карточка профиля. */
export const CREST_KEY = "own_crest";

/**
 * Строка «Своего герба» из goals[] /app/me: {key: "own_crest", kind: "once", line, img}. Её не
 * пропускает normGoal (вид "once" — не ступени и не уровни): в общие цели экранов она не идёт,
 * у неё своя карточка на своём профиле. Нет строки — нет карточки.
 */
export const normCrest = (goals: unknown): AchCrestHint | undefined => {
    if (!Array.isArray(goals)) return undefined;
    for (const v of goals) {
        if (!isObj(v) || v.key !== CREST_KEY) continue;
        const line = str(v.line, 400);
        if (!line) continue;
        return compact({ line, img: imgId(v.img) });
    }
    return undefined;
};

/** Тело ответа /me (/app/me) → свой вид. Не та форма — null. */
export const normMe = (body: unknown): AchievementsView | null => {
    if (!isObj(body)) return null;
    const view: AchievementsView = {
        self: true,
        form: normForm(body.form),
        items: list(body.items, normItem),
        collection: list(body.collection, normRow),
    };
    // Уровни — только если сервер их прислал (смотрящий видит баллы v8.2, §3.5).
    if (isObj(body.levels)) {
        const levels: Partial<Record<AchSphere, AchLevel>> = {};
        ACH_SPHERES.forEach((s) => {
            const lv = normLevel((body.levels as Record<string, unknown>)[s]);
            if (lv) levels[s] = lv;
        });
        view.levels = levels;
    }
    const ladders = normLadders(body.ladders, normLadder);
    if (ladders) view.ladders = ladders;
    const top = normTop(body.top);
    if (top.length) view.top = top;
    // Строки «что сделать → что получите» (/app/me): цели — только если сервер их уже присылает.
    if (Array.isArray(body.goals)) view.goals = list(body.goals, normGoal);
    if (Array.isArray(body.nearest)) view.nearest = list(body.nearest, normGoal).map((g) => ({ ...g, started: true }));
    // Профиль v2 (§1.3): «Ближе всего» и строка «Своего герба» — только если сервер их присылает.
    if (Array.isArray(body.closest)) view.closest = list(body.closest, normGoal).map((g) => ({ ...g, started: true }));
    const crest = normCrest(body.goals);
    if (crest) view.crest = crest;
    return view;
};

/** Адрес своего вида: строки — на языке страницы (cn → zh, незнакомый → en). */
export const myAchievementsUrl = (lang: string): string =>
    `${BACKEND_API_URL}/api/achievements/app/me?lang=${encodeURIComponent(achLang(lang))}`;

/**
 * GET /api/achievements/app/me. 404 (и 401/403) — null: достижений для этого человека нет.
 * timeoutMs — сколько ждать (обычно ACH_TIMEOUT_MS; «Повторить» — ACH_RETRY_TIMEOUT_MS).
 */
export const fetchMyAchievements = async (
    lang: string,
    signal?: AbortSignal,
    timeoutMs: number = ACH_TIMEOUT_MS,
): Promise<AchievementsView | null> => {
    const token = siteSessionToken();
    if (!token) return null;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const onAbort = () => ctrl.abort();
    signal?.addEventListener("abort", onAbort);
    let res: Response;
    try {
        res = await fetch(myAchievementsUrl(lang), {
            headers: { Authorization: `Bearer ${token}` },
            signal: ctrl.signal,
        });
    } catch {
        throw new AchievementsApiError(null);
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
    }
    if (res.status === 404 || res.status === 401 || res.status === 403) return null;
    if (!res.ok) throw new AchievementsApiError(res.status);
    let body: unknown;
    try {
        body = await res.json();
    } catch {
        throw new AchievementsApiError(res.status, "achievements: bad json");
    }
    const view = normMe(body);
    if (!view) throw new AchievementsApiError(res.status, "achievements: bad response");
    return view;
};
